import { useEffect, useState } from 'react';
import { Formik, FormikProps } from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';
import { logEvent } from '@amplitude/analytics-browser';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Typography from '../../../componentsv2/Typography';
import TopBar from '../../../componentsv2/TopBar';
import MarcoLogo from '../../../componentsv2/MarcoLogo';
import CoBrandedLogo from '../../../componentsv2/CoBrandedLogo';
import SignupSkeleton from './skeleton';
import { SelectField, TextField } from '../../../componentsv2/Field';
import Button from '../../../componentsv2/Button';
import Form from '../../../componentsv2/Form';
import PhoneNumberField from '../../../componentsv2/Field/PhoneNumberField';
import CountryFlag from '../../../componentsv2/CountryFlag';
import ErrorMessage from '../../../components/ErrorMessage';
import { ButtonSizes } from '../../../componentsv2/Button';
import { InvoicesTableIcon } from '../../../components/Icons';
import { useTranslation } from '../../../hooks/i18n';
import { useUserLocation } from '../../../hooks/useUserLocation';
import { useAnonymousUser } from '../../../hooks/anonymous';
import { usePartnerByReferralId } from '../../../hooks/usePartners';
import { signUp as signUpThunk, useAppDispatch, useAppSelector } from '../../../store';
import { SignupSchema } from '../../../schemas';
import { Nullable, SignupCountryKeys } from '../../../types';
import { SignupFields } from '../../../types/signup';
import { Referral } from '../../../types/signup';
import { OTHER_PHONE_COUNTRY, phoneCountryCodes } from '../../../utils/helpers';
import { removeAppsCurrentStep } from '../../../utils/common';
import { signupEvents } from '../../../constants/events';
import { APP_FLOW, VERIFY_EMAIL_SENT } from '../../../constants/auth';
import { COLORS } from '../../../themev2';

const logSignupSubmit = ({ firstName, lastName }: SignupFields, language: string, page: Nullable<string>) => {
  logEvent(signupEvents.FORM_SUBMITTED, {
    Name: `${firstName} ${lastName}`,
    Language: language,
    Page: page,
  });
};

const Signup = () => {
  const { t } = useTranslation(['user', 'common']);
  const { language } = useUserLocation();
  const { location } = useAnonymousUser();
  const { error, status } = useAppSelector((state) => state.auth);
  const isLoading = status === 'loading';
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [referral, setReferral] = useState<Referral>({ id: null, source: null });
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const utmRef = queryParams.get('utm_ref');
  const utmSource = queryParams.get('utm_source');
  const referralClient = queryParams.get('referral_client');
  const page = queryParams.get('page');
  const hubspotCompanyId = queryParams.get('hs_company_id');
  const referralId = queryParams.get('referral_id');
  const { partner, isLoadingPartner } = usePartnerByReferralId(referralId);
  const [captcha, setCaptcha] = useState<string>();

  useEffect(() => {
    setReferral({ id: referralId || utmRef, source: utmSource });
    logEvent(signupEvents.VIEW_PAGE, {
      URL_utm_ref: utmRef,
      URL_utm_source: utmSource,
      URL_referral_client: referralClient,
      URL_referral_id: referralId,
    });
  }, [utmRef, utmSource, referralClient, referralId]);

  if (isLoadingPartner) return <SignupSkeleton />;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <TopBar>
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CoBrandedLogo partnerLogoUrl={partner?.logoUrl} partnerName={partner?.name}>
            <IconButton href="/">
              <MarcoLogo />
            </IconButton>
          </CoBrandedLogo>
        </Box>
      </TopBar>
      <Box component="main" sx={{ flexGrow: 1, paddingTop: '1em', display: 'flex' }}>
        <Grid container spacing={2}>
          <Grid item xs={6} sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1em',
                textAlign: 'center',
              }}
            >
              <InvoicesTableIcon />
              <Box sx={{ position: 'relative', top: '-5em', maxWidth: '60%' }}>
                <Typography sx={{ fontSize: '24px', marginBottom: '0.5em' }}>
                  {t('common:signup.whyMarco')}
                </Typography>
                <Typography sx={{ color: COLORS.gray[1100], fontSize: '18px' }}>
                  {t('common:signup.description')}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            lg={6}
            sx={{
              backgroundColor: { xs: 'inherit', md: '#FBFCFE' },
              paddingBottom: '1em',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Grid container rowSpacing={2} justifyContent="center">
              <Grid item xs={12}>
                <Typography variant="h5" sx={{ fontWeight: 600, textAlign: 'center' }}>
                  {t('common:signup.title')}
                </Typography>
              </Grid>
              <Grid item xs={10} md={8}>
                <Formik
                  validateOnBlur={false}
                  validateOnChange
                  initialValues={{
                    // country: location,
                    firstName: '',
                    lastName: '',
                    contactPhoneCountry: location,
                    contactPhone: '',
                    email: '',
                    // companyName: '',
                  }}
                  validationSchema={SignupSchema(t)}
                  validate={(values) => {
                    if (!captcha) return { captcha: t('common:signup.form.errors.captcha') };
                    if (values.contactPhoneCountry === OTHER_PHONE_COUNTRY) return;
                    const phoneLength = phoneCountryCodes[values.contactPhoneCountry].placeholder.replace(
                      /[() -]/g,
                      ''
                    ).length;
                    const isBelgium = values.contactPhoneCountry === 'be';
                    const minLength = isBelgium ? phoneLength - 1 : phoneLength;
                    if (values.contactPhone?.toString().length < minLength)
                      return { contactPhone: t('common:signup.form.phoneNumber.errors.invalid') };
                  }}
                  onSubmit={(values) => {
                    // format fields; contactPhoneCountry is kept so the backend can
                    // skip phone-number validation when it's OTHER_PHONE_COUNTRY
                    const formattedValues = {
                      ...values,
                      contactPhone:
                        values.contactPhoneCountry === OTHER_PHONE_COUNTRY
                          ? `+${values.contactPhone}`
                          : `+${phoneCountryCodes[values.contactPhoneCountry].code}${values.contactPhone}`,
                      // country: backendCountryValues[values.country],
                      captchaToken: captcha!,
                    };
                    dispatch(
                      signUpThunk({
                        values: formattedValues,
                        referral,
                        page,
                        language,
                        resetCaptcha: () => {
                          (window as any).grecaptcha.reset();
                        },
                        referralClient,
                        hubspotCompanyId,
                      })
                    )
                      .unwrap()
                      .then((_) => {
                        logSignupSubmit(formattedValues, language, page);
                        navigate(VERIFY_EMAIL_SENT, { state: { email: values.email } });
                        localStorage.removeItem(APP_FLOW);
                        removeAppsCurrentStep();
                      });
                  }}
                >
                  {({ values, setFieldValue, submitCount }: FormikProps<any>) => {
                    return (
                      <Form name="form-signup" id="form-signup" trackingEvent={signupEvents.EDIT_FIELD}>
                        <Grid container rowSpacing={1}>
                          <Grid item xs={6}>
                            <TextField
                              label={t('user:signup.firstName.label')}
                              name="firstName"
                              placeholder={t('user:signup.firstName.placeholder')}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              label={t('user:signup.lastName.label')}
                              name="lastName"
                              placeholder={t('user:signup.lastName.placeholder')}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Grid container spacing={0}>
                              <Grid item xs={4} md={3}>
                                <SelectField
                                  label={t('common:signup.form.phoneNumber.placeholder')}
                                  name="contactPhoneCountry"
                                  options={[
                                    ...Object.entries(phoneCountryCodes)
                                      .map(([key, value]) => ({
                                        value: key,
                                        label: value.code.toString(),
                                      }))
                                      .sort((a, b) => a.value.localeCompare(b.value)),
                                    {
                                      value: OTHER_PHONE_COUNTRY,
                                      label: t('common:signup.form.phoneNumber.otherOption'),
                                    },
                                  ]}
                                  renderOption={({ label, value }) => (
                                    <MenuItem key={value} value={value}>
                                      {value !== OTHER_PHONE_COUNTRY && (
                                        <CountryFlag
                                          countryCode={value}
                                          size={24}
                                          style={{ marginRight: '8px' }}
                                        />
                                      )}
                                      {value === OTHER_PHONE_COUNTRY ? label : `(+${label})`}
                                    </MenuItem>
                                  )}
                                  renderValue={(value) => {
                                    return (
                                      <Typography textAlign="center">
                                        {value === OTHER_PHONE_COUNTRY
                                          ? t('common:signup.form.phoneNumber.otherOption')
                                          : `(+${phoneCountryCodes[value as SignupCountryKeys].code})`}
                                      </Typography>
                                    );
                                  }}
                                  sx={{
                                    '& fieldset': {
                                      borderRadius: '8px 0 0 8px',
                                      borderColor: COLORS.gray[900],
                                    },
                                  }}
                                />
                              </Grid>
                              <Grid item xs={8} md={9}>
                                {values.contactPhoneCountry === OTHER_PHONE_COUNTRY ? (
                                  <TextField
                                    name="contactPhone"
                                    fullWidth
                                    label={
                                      <span style={{ visibility: 'hidden' }}>
                                        {t('common:signup.form.phoneNumber.placeholder')}
                                      </span>
                                    }
                                    placeholder={t('common:signup.form.phoneNumber.otherPlaceholder')}
                                    onChange={(value: string) =>
                                      setFieldValue('contactPhone', value.replace(/\D/g, ''))
                                    }
                                    sx={{
                                      '& fieldset': {
                                        borderRadius: '0 8px 8px 0',
                                        borderColor: COLORS.gray[900],
                                      },
                                    }}
                                  />
                                ) : (
                                  <PhoneNumberField
                                    name="contactPhone"
                                    label="Phone number"
                                    hideLabel
                                    fullWidth
                                    value={values.contactPhone}
                                    format={phoneCountryCodes[
                                      values.contactPhoneCountry as SignupCountryKeys
                                    ].placeholder.replace(/[0-9]/g, '#')}
                                    placeholder={
                                      phoneCountryCodes[values.contactPhoneCountry as SignupCountryKeys]
                                        .placeholder
                                    }
                                    onChange={(value) => setFieldValue('contactPhone', value.floatValue)}
                                    sx={{
                                      borderRadius: '0 8px 8px 0',
                                      borderColor: COLORS.gray[900],
                                    }}
                                  />
                                )}
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              name="email"
                              fullWidth
                              placeholder={t('common:signup.form.email.placeholder')}
                              label={t('common:signup.form.email.label')}
                            />
                          </Grid>
                          {!!error && (
                            <Grid item xs={12}>
                              <ErrorMessage errorMessage={t(`common:signup.form.errors.${error}`)} />
                            </Grid>
                          )}
                          {!captcha && submitCount > 0 && (
                            <Grid item xs={12}>
                              <ErrorMessage errorMessage={t('common:signup.form.errors.captcha')} />
                            </Grid>
                          )}
                          <Grid
                            item
                            xs={12}
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              marginY: '8px !important',
                            }}
                          >
                            <ReCAPTCHA
                              sitekey={process.env.REACT_APP_RECAPTCHA_KEY as string}
                              onChange={(value) => {
                                setCaptcha(value as string);
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sx={{}}>
                            <Button
                              fullWidth
                              color="secondary"
                              size={ButtonSizes.Large}
                              isLoading={isLoading}
                              isSubmit
                            >
                              {t('common:signup.form.submit')}
                            </Button>
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              gap: '8px',
                              paddingTop: '1em !important',
                            }}
                          >
                            <Typography>{t('common:signup.alreadyHaveAnAccount')}</Typography>
                            <Link to={'/login'} style={{ color: COLORS.indigo[500] }}>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  lineHeight: '24px',
                                  textAlign: 'left',
                                  color: COLORS.indigo[500],
                                }}
                              >
                                {t('common:signup.login')}
                              </Typography>
                            </Link>
                          </Grid>
                        </Grid>
                      </Form>
                    );
                  }}
                </Formik>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Signup;
